// src/components/MeusResultados.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db, auth, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, doc, getDoc, collectionGroup, onSnapshot, updateDoc } from 'firebase/firestore';
import Select from 'react-select';
import { FaUser, FaUserTie, FaUsers, FaChartLine, FaCommentDots, FaChevronDown, FaChevronUp, FaSearch, FaLightbulb, FaSpinner, FaExclamationTriangle, FaLock, FaCheckCircle, FaSync } from 'react-icons/fa';

import '../styles/MeusResultados.css';
import '../styles/Ouvidoria.css'; // Reutilizamos estilos daqui

// --- MODAL DE SUGESTÕES DE PDI ---
const PdiInsightsModal = ({ isOpen, onClose, isLoading, error, analysisResult, onSave, onReanalyze, hasCredits }) => {
    if (!isOpen) return null;

    const [isSaving, setIsSaving] = useState(false);

    const handleSaveClick = async () => {
        setIsSaving(true);
        await onSave();
        setIsSaving(false);
    };

    const renderContent = () => {
        if (isLoading) return <div className="ai-feedback-state"><FaSpinner className="spinner" /><p>Analisando seus resultados e gerando seu PDI...</p></div>;
        if (error) return <div className="ai-feedback-state error">{error.isLimitError ? <FaLock /> : <FaExclamationTriangle />}<h4>{error.isLimitError ? "Limite Atingido" : "Ocorreu um Erro"}</h4><p>{error.message}</p></div>;
        if (!analysisResult) return <div className="ai-feedback-state"><FaSearch /><p>Nenhuma sugestão de PDI gerada.</p></div>;

        return (
            <div>
                <div className="insight-card">
                    <div className="insight-card-header"><h4>🎯 Objetivo Principal Sugerido</h4></div>
                    <div className="insight-card-body"><p>{analysisResult.objetivoPrincipal}</p></div>
                </div>
                <h5 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Metas Sugeridas:</h5>
                <div className="ai-insights-grid">
                    {(analysisResult.metas || []).map((meta, index) => (
                         <div key={index} className="insight-card">
                             <div className="insight-card-header"><h5>{meta.descricao}</h5></div>
                             <div className="insight-card-body">
                                 <p><strong>Prazo Sugerido:</strong> {new Date(meta.prazo + 'T12:00:00Z').toLocaleDateString('pt-BR')}</p>
                                 {meta.observacoes && <p style={{marginTop: '10px'}}><strong>Observações:</strong> {meta.observacoes}</p>}
                             </div>
                         </div>
                    ))}
                </div>
            </div>
        );
    };

return (
        <div className="modal-overlay-ouvidoria" onClick={onClose}>
            <div className="modal-content-ouvidoria large" onClick={e => e.stopPropagation()}>
                <button className="modal-close-button" onClick={onClose}>&times;</button>
                <div className="modal-header-with-action"><h3><FaLightbulb /> Sugestões para seu PDI com IA</h3></div>
                <div className="ai-insights-container">{renderContent()}</div>
                {!isLoading && !error && analysisResult && (
                    <div className="pdi-modal-footer">
                        <button onClick={onReanalyze} className="btn btn-secondary" disabled={!hasCredits || isSaving} title={!hasCredits ? "Créditos esgotados" : "Gerar novas sugestões"}>
                            <FaSync /> Gerar Novamente
                        </button>
                        <button onClick={handleSaveClick} className="btn btn-success" disabled={isSaving}>
                            {isSaving ? <><FaSpinner className="spinner-inline" /> Salvando...</> : <><FaCheckCircle /> Salvar este PDI</>}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- MODAL DE SUCESSO ---
const SuccessModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{maxWidth: '450px', textAlign: 'center'}}>
                <div className="success-message" style={{padding: '2rem 0'}}>
                    <FaCheckCircle size={40} style={{color: 'var(--success-color)'}}/>
                    <h3 style={{marginTop: '1rem'}}>PDI Atualizado!</h3>
                    <p>As sugestões foram salvas. Você já pode vê-las no seu Painel do Colaborador.</p>
                    <button onClick={onClose} className="btn btn-primary" style={{marginTop: '1rem'}}>Ok</button>
                </div>
            </div>
        </div>
    );
};

// Estilos para o Select (whitelabel)
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '50px', border: '1px solid var(--border-color, #ced4da)', borderRadius: '8px',
        boxShadow: state.isFocused ? `0 0 0 1px var(--primary-color, #007bff)` : 'none',
        '&:hover': { borderColor: 'var(--primary-color, #007bff)' },
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? 'var(--primary-color, #007bff)' : state.isFocused ? 'var(--primary-color-light, #e0f2ff)' : '#fff',
        color: state.isSelected ? '#fff' : 'var(--text-color-dark, #212529)',
        cursor: 'pointer'
    }),
    menu: (base) => ({ ...base, zIndex: 100 })
};

// Função auxiliar para calcular nota
const calculateWeightedScore = (answers, form, scales, baseScale = 10) => {
    if (!answers || !form || !scales || Object.keys(scales).length === 0 || Object.keys(answers).length === 0) return null;
    let totalNormalizedScore = 0, totalWeight = 0;
    form.forEach((question) => {
        const answer = answers[question.questionId], weight = parseFloat(question.weight), scale = scales[question.type];
        if (scale && !isNaN(weight) && weight > 0 && answer && answer.score !== undefined) {
            const score = parseFloat(answer.score), maxScore = scale.max;
            if (!isNaN(score)) {
                const normalizedScore = maxScore > 0 ? (score / maxScore) * baseScale : 0;
                totalNormalizedScore += normalizedScore * weight;
                totalWeight += weight;
            }
        }
    });
    return totalWeight === 0 ? null : (totalNormalizedScore / totalWeight).toFixed(2);
};

const MeusResultados = ({ usageData }) => {
    const [loading, setLoading] = useState(true);
    const [allCyclesDetails, setAllCyclesDetails] = useState([]);
    const [expandedCycleId, setExpandedCycleId] = useState(null);
    const [cycleFilterOptions, setCycleFilterOptions] = useState([]);
    const [selectedFilter, setSelectedFilter] = useState(null);
    const [error, setError] = useState('');
    const currentUser = auth.currentUser;

    // --- Estados para IA e UI ---
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [analysisError, setAnalysisError] = useState(null);
    const [cachedPdiAnalyses, setCachedPdiAnalyses] = useState({});
    const [selectedCycleForPDI, setSelectedCycleForPDI] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    useEffect(() => {
        if (!currentUser?.uid || allCyclesDetails.length === 0) return;
        const availableCache = {};
        allCyclesDetails.forEach(cycle => {
            const cacheKey = `pdiAnalysis_${currentUser.uid}_${cycle.id}`;
            const cachedData = localStorage.getItem(cacheKey);
            if (cachedData) {
                try {
                    const cacheAge = (new Date() - new Date(JSON.parse(cachedData).timestamp)) / (1000 * 3600 * 24);
                    if (cacheAge < 30) availableCache[cycle.id] = true;
                } catch (e) { localStorage.removeItem(cacheKey); }
            }
        });
        setCachedPdiAnalyses(availableCache);
    }, [allCyclesDetails, currentUser]);

    useEffect(() => {
        if (!currentUser) return;
        const fetchAllUserCycleDetails = async () => {
            setLoading(true);
            try {
                const userDocSnap = await getDoc(doc(db, 'users', currentUser.uid));
                const empresaId = userDocSnap.data()?.empresa_id;
                if (!empresaId) throw new Error("Empresa não encontrada.");

                const participantsQuery = query(
                    collectionGroup(db, 'participants'), 
                    where('userId', '==', currentUser.uid),
                    where('empresaId', '==', empresaId)
                );
                
                const participantDocsSnapshot = await getDocs(participantsQuery);
                if (participantDocsSnapshot.empty) { setLoading(false); return; }

                const cyclesDetailsPromises = participantDocsSnapshot.docs.map(async (pDoc) => {
                    const cycleRef = pDoc.ref.parent.parent;
                    const cycleSnap = await getDoc(cycleRef);
                    if (cycleSnap.exists()) {
                        const cycleData = cycleSnap.data(), participantData = pDoc.data();
                        const canShowResults = participantData.status === 'delivered' || participantData.resultsReleased === true;
                        
                        const details = { 
                            id: cycleSnap.id, 
                            name: cycleData.name, 
                            canShowResults, 
                            finalScore: participantData.finalScore,
                            endDate: cycleData.endDate,
                            baseScale: cycleData.baseScale,
                            resultsVisibility: cycleData.resultsVisibility
                        };
                        
                        if (!canShowResults) return details;

                        const responsesSnap = await getDocs(query(collection(db, 'Empresas', empresaId, 'evaluationCycles', cycleSnap.id, 'responses'), where('evaluatedUserId', '==', currentUser.uid), where('status', '==', 'submitted')));
                        const allResponses = responsesSnap.docs.map(d => d.data());
                        const scaleIds = [...new Set(cycleData.form.map(q => q.type).filter(Boolean))];
                        const scalesMap = { 'default_0_5': { max: 5 }, 'default_0_10': { max: 10 } };
                        if (scaleIds.length > 0) { const scalesQuery = query(collection(db, 'Empresas', empresaId, 'evaluationScales'), where('__name__', 'in', scaleIds)); (await getDocs(scalesQuery)).forEach(doc => { scalesMap[doc.id] = doc.data(); }); }
                        
                        // =================== INÍCIO DA CORREÇÃO DE SEGURANÇA ===================
                        const visibility = {
                            showManagerComments: true,
                            showPeerComments: true,
                            ...(cycleData.resultsVisibility || {})
                        };
                        
                        const scores = { self: null, manager: null, peers: [] };
                        const commentsByQuestion = {};
                        cycleData.form.forEach(q => { commentsByQuestion[q.questionId] = { text: q.text, comments: [] }; });

                        allResponses.forEach(res => {
                            const score = calculateWeightedScore(res.answers, cycleData.form, scalesMap, cycleData.baseScale);
                            if (res.relationship === 'self') scores.self = score; 
                            else if (res.relationship === 'manager') scores.manager = score; 
                            else if (res.relationship === 'peer' && score) scores.peers.push(parseFloat(score));

                            let canShowComments = false;
                            if (res.relationship === 'self') {
                                canShowComments = true;
                            } else if (res.relationship === 'manager' && visibility.showManagerComments) {
                                canShowComments = true;
                            } else if (res.relationship === 'peer' && visibility.showPeerComments) {
                                canShowComments = true;
                            }

                            if (canShowComments) {
                                Object.entries(res.answers || {}).forEach(([qId, answer]) => {
                                    if (commentsByQuestion[qId] && answer.justification) {
                                        commentsByQuestion[qId].comments.push({ 
                                            relationship: res.relationship, 
                                            text: answer.justification 
                                        });
                                    }
                                });
                            }
                        });
                        // =================== FIM DA CORREÇÃO DE SEGURANÇA ===================

                        const peerAvg = scores.peers.length > 0 ? (scores.peers.reduce((a, b) => a + b, 0) / scores.peers.length).toFixed(2) : null;
                        
                        details.scores = { self: scores.self, manager: scores.manager, peers: peerAvg };
                        details.comments = Object.values(commentsByQuestion).filter(q => q.comments.length > 0);
                        
                        return details;
                    } 
                    return null;
                });
                const allDetails = (await Promise.all(cyclesDetailsPromises)).filter(Boolean).sort((a, b) => b.endDate.toDate() - a.endDate.toDate());
                setAllCyclesDetails(allDetails);
                const defaultOption = { value: 'all', label: 'Todos os Ciclos' };
                const options = allDetails.map(cycle => ({ value: cycle.id, label: cycle.name }));
                setCycleFilterOptions([defaultOption, ...options]);
                setSelectedFilter(defaultOption);
            } catch (err) { console.error("Erro ao buscar ciclos:", err); setError("Não foi possível carregar seus resultados."); }
            finally { setLoading(false); }
        };
        fetchAllUserCycleDetails();
    }, [currentUser]);

    const filteredCycles = useMemo(() => selectedFilter?.value === 'all' || !selectedFilter ? allCyclesDetails : allCyclesDetails.filter(c => c.id === selectedFilter.value), [selectedFilter, allCyclesDetails]);

    const handleToggleDetails = (cycleId) => setExpandedCycleId(prevId => (prevId === cycleId ? null : cycleId));
    
    const getRelationshipInfo = (relationship) => {
        switch (relationship) {
            case 'self': return { icon: <FaUser />, label: 'Autoavaliação' };
            case 'manager': return { icon: <FaUserTie />, label: 'Gestor' };
            case 'peer': return { icon: <FaUsers />, label: 'Pares' };
            default: return { icon: <FaUser />, label: 'Outro' };
        }
    };

    const handleGeneratePdi = async (cycleId, ignoreCache = false) => {
        setSelectedCycleForPDI(cycleId);
        setIsAiModalOpen(true);
        setIsAnalyzing(true);
        setAnalysisError(null);
        setAnalysisResult(null);
        const cacheKey = `pdiAnalysis_${currentUser.uid}_${cycleId}`;
        if (!ignoreCache && localStorage.getItem(cacheKey)) { setAnalysisResult(JSON.parse(localStorage.getItem(cacheKey)).result); setIsAnalyzing(false); return; }
        try {
            const generatePdiWithAI = httpsCallable(functions, 'generatePdiWithAI');
            const response = await generatePdiWithAI({ cycleId });
            if (response.data.success) {
                setAnalysisResult(response.data.pdi);
                localStorage.setItem(cacheKey, JSON.stringify({ result: response.data.pdi, timestamp: new Date().toISOString() }));
                setCachedPdiAnalyses(prev => ({ ...prev, [cycleId]: true }));
            }
        } catch (err) { setAnalysisError({ isLimitError: err.code === 'functions/permission-denied', message: err.message }); }
        finally { setIsAnalyzing(false); }
    };

    const handleSavePdi = async () => {
        if (!analysisResult || !currentUser) return;
        try {
            await updateDoc(doc(db, 'usersprivado', currentUser.uid), {
                'pdi.objetivoPrincipal': analysisResult.objetivoPrincipal,
                'pdi.metas': analysisResult.metas,
                'pdi.ultimaAtualizacao': new Date()
            });
            setIsAiModalOpen(false);
            setShowSuccessModal(true);
        } catch (err) { setAnalysisError({ isLimitError: false, message: `Erro ao salvar: ${err.message}` }); }
    };

    if (loading) return <div className="meus-resultados-container"><p>Carregando seus resultados...</p></div>;
    const hasCredits = usageData ? usageData.used < usageData.total : false;

    return (
        <div className="meus-resultados-container">
            <div className="meus-resultados-header"><h1><FaChartLine /> Meus Resultados de Desempenho</h1><p>Aqui estão todos os ciclos de avaliação que você participou.</p></div>
            <div className="cycle-filter-container">
                 <div><label htmlFor="cycle-select">Filtrar por Ciclo</label><Select id="cycle-select" value={selectedFilter} onChange={setSelectedFilter} options={cycleFilterOptions} styles={customSelectStyles} /></div>
            </div>
            {error && <p className="error-message">{error}</p>}
            <div className="cycles-list-container">
                {filteredCycles.length > 0 ? (
                    filteredCycles.map(cycle => {
                        const isExpanded = expandedCycleId === cycle.id;
                        const isCached = !!cachedPdiAnalyses[cycle.id];
                        const canGeneratePdi = cycle.canShowResults && (hasCredits || isCached);
                        
                        const visibility = {
                            showFinalScore: true,
                            showManagerScore: true,
                            showPeerScore: true,
                            showSelfScore: true,
                            showManagerComments: true,
                            showPeerComments: true,
                            ...(cycle.resultsVisibility || {})
                        };

                        return (
                            <div key={cycle.id} className="result-cycle-card">
                                <div className="result-card-header" onClick={() => handleToggleDetails(cycle.id)}>
                                    <h2>{cycle.name}</h2>
                                    <div className="header-right-content">
                                        {visibility.showFinalScore && cycle.canShowResults && 
                                            <div className="final-score-badge">
                                                <span>Nota Final</span>
                                                <strong>{cycle.finalScore?.toFixed(2) ?? 'N/A'}</strong>
                                            </div>
                                        }
                                        {cycle.canShowResults && <button className="btn-ai-pdi" onClick={(e) => { e.stopPropagation(); handleGeneratePdi(cycle.id); }} disabled={!canGeneratePdi} title={!canGeneratePdi ? "Créditos esgotados" : "Gerar PDI com IA"}><FaLightbulb /> <span>PDI com IA</span></button>}
                                        <button className="expand-button">{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</button>
                                    </div>
                                </div>
                                {isExpanded && (cycle.canShowResults ? (
                                    <div className="result-details-grid">
                                        <div className="score-breakdown">
                                            <h3>Detalhamento das Notas</h3>
                                            {Object.entries(cycle.scores || {}).map(([rel, score]) => {
                                                if (!score) return null;
                                                const keyMap = { self: 'showSelfScore', manager: 'showManagerScore', peers: 'showPeerScore' };
                                                if (visibility[keyMap[rel]]) {
                                                    return (
                                                        <div key={rel} className="relationship-score">
                                                            <div className="relationship-label">{getRelationshipInfo(rel).icon} {getRelationshipInfo(rel).label}</div>
                                                            <span className="score-value">{score} / {cycle.baseScale}</span>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                        <div className="comments-section">
                                            <h3><FaCommentDots /> Comentários Recebidos</h3>
                                            {(cycle.comments || []).length > 0 ? cycle.comments.map((q, i) => {
                                                // O filtro aqui se torna uma segunda camada de segurança, mas o principal já foi feito
                                                const visibleComments = q.comments.filter(c => {
                                                    if (c.relationship === 'manager') return visibility.showManagerComments;
                                                    if (c.relationship === 'peer') return visibility.showPeerComments;
                                                    if (c.relationship === 'self') return true;
                                                    return false;
                                                });

                                                if (visibleComments.length === 0) return null;

                                                return (
                                                    <div key={i} className="comment-group">
                                                        <h4>{q.text}</h4>
                                                        {visibleComments.map((c, ci) => 
                                                            <div key={ci} className="comment-item">
                                                                <p className="comment-question">{getRelationshipInfo(c.relationship).icon} Feedback de: <strong>{getRelationshipInfo(c.relationship).label}</strong></p>
                                                                <p className="comment-text">"{c.text}"</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            }) : <p className="no-comment">Nenhum comentário.</p>}
                                        </div>
                                    </div>) : (<div className="results-not-released"><p>Os resultados deste ciclo ainda não foram liberados.</p></div>)
                                )}
                            </div>
                        );
                    })
                ) : <div className="no-results-found"><FaChartLine size={40} /><h2>Nenhum ciclo para exibir</h2><p>Você ainda não participou de nenhum ciclo ou não há resultados disponíveis.</p></div>}
            </div>
            <PdiInsightsModal isOpen={isAiModalOpen} onClose={() => setIsAiModalOpen(false)} isLoading={isAnalyzing} error={analysisError} analysisResult={analysisResult} onSave={handleSavePdi} onReanalyze={() => handleGeneratePdi(selectedCycleForPDI, true)} hasCredits={hasCredits} />
            <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} />
        </div>
    );
};

export default MeusResultados;